import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import Cookies from "universal-cookie";
import middleware from "../../Api/Middleware";
import PageTemplate from "../../Templates/PageTemplate";
import TablePreset from "../../Table/TablePreset";
import CheckboxHeader from "../../Table/TableComponents/CheckboxHeader";
import CheckboxCell from "../../Table/TableComponents/CheckboxCell";
import SortableHeader from "../../Table/TableComponents/SortableHeader";
import ToastError from "../../Toasts/ToastError";
import ToastSuccess from "../../Toasts/ToastSuccess";
import Modal from "react-bootstrap/Modal";
import { useTable } from "react-table";
import TDSTextNavigation from "../../Templates/TDSTextNavigation";
import { getLanguageFromCountryCode } from "../../../utils";

const account_status_definer = {
  0: { color: "red", text: "Geblokkeerd" },
  1: { color: "green", text: "Actief" },
};

const account_role_definer = {
  0: { color: "#A0A0A0", text: "Alleen bestellingen" },
  1: { color: "#007BFF", text: "Medewerker" },
  2: { color: "#17A2B8", text: "Leidinggevende" },
  3: { color: "purple", text: "Manager" },
  4: { color: "darkblue", text: "Admin" },
};

const ACCOUNT_STATUS = {
  ACTIVE: 1,
  SUSPENDED: 0,
};

const ACTION_TYPES = {
  SUSPEND: "suspend",
  ACTIVATE: "activate",
};

const Users = ({ userRole }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const cookies = new Cookies();

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 10,
    max_items: 0,
    begin_of_page: 0,
    end_of_page: 0,
  });
  const [order, setOrder] = useState({ orderBy: "", direction: "" });
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [searchBar, setSearchBar] = useState("");
  const [timerId, setTimerId] = useState(null);
  const [show, setShow] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const searchQuery = searchBar.trim() ? `&searchTerm=${encodeURIComponent(searchBar.trim())}` : '';
      const response = await middleware.get(
        `sp/usersTableAll?account_status=1&page=${pagination.page}&page_size=${pagination.page_size}${searchQuery}&noSearch=["password","api_token","updated_at","created_at"]${
          order.orderBy && order.direction
            ? `&orderBy=${order.orderBy}&orderDirection=${order.direction}`
            : ""
        }`
      );
      setUsers(response.data.data);
      setPagination((prev) => ({
        ...prev,
        max_items: response.data.total,
        begin_of_page: response.data.from,
        end_of_page: response.data.to,
      }));
      setLoading(false);
    } catch (error) {
      if (error.response && error.response.status === 401) {
        cookies.remove("token", { path: "/" });
        navigate("/");
      } else {
        ToastError("Er is een fout opgetreden bij het ophalen van gebruikers");
      }
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [searchBar, pagination.page, pagination.page_size, order]);

  const handleUserSelection = (userId, isSelected) => {
    if (isSelected) {
      setSelectedUserIds([...selectedUserIds, userId]);
    } else {
      setSelectedUserIds(selectedUserIds.filter((id) => id !== userId));
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: (
          <CheckboxHeader
            currentItem={users}
            selectedIds={selectedUserIds}
            setSelectedIds={setSelectedUserIds}
            pk={"user_id"}
          ></CheckboxHeader>
        ),
        accessor: "select",
        Cell: ({ row }) => {
          return (
            <CheckboxCell
              row={row}
              handleSelection={handleUserSelection}
              selectedIds={selectedUserIds}
              pk={"user_id"}
            />
          );
        },
      },
      {
        Header: () => (
          <SortableHeader
            currentItem={order}
            setItem={setOrder}
            field="username"
            pos={"start"}
          >
            Gebruiker
          </SortableHeader>
        ),
        accessor: "username",
        Cell: ({ row }) => {
          return (
            <TDSTextNavigation
              text={row.original.username}
              link={`/algemene/instellingen/gebruikers/bewerken/${row.original.user_id}`}
            />
          );
        },
      },
      {
        Header: () => (
          <SortableHeader
            currentItem={order}
            setItem={setOrder}
            field="email"
            pos={"start"}
          >
            Email
          </SortableHeader>
        ),
        accessor: "email",
        Cell: ({ row }) => {
          return row.original.email;
        },
      },
      {
        Header: () => (
          <SortableHeader
            currentItem={order}
            setItem={setOrder}
            field="language"
            pos={"start"}
          >
            Taal
          </SortableHeader>
        ),
        accessor: "language",
        Cell: ({ row }) => {
          return getLanguageFromCountryCode(row.original.language) || '-' 
        },
      },
      {
        Header: () => (
          <SortableHeader
            currentItem={order}
            setItem={setOrder}
            field="role"
            pos={"start"}
          >
            Rol
          </SortableHeader>
        ),
        accessor: "role",
        Cell: ({ row }) => {
          const roleNumber = row.original.role;
          const role = account_role_definer[roleNumber];

          if (!role) {
            return <span style={{ color: "#000000" }}>Onbekend</span>;
          }

          return <span style={{ color: role.color }}>{role.text}</span>;
        },
      },
      {
        Header: () => (
          <SortableHeader
            currentItem={order}
            setItem={setOrder}
            field="account_status"
            pos={"start"}
          >
            Status
          </SortableHeader>
        ),
        accessor: "account_status",
        Cell: ({ row }) => {
          const status = account_status_definer[row.original.account_status];
          return <span style={{ color: status.color }}>{status.text}</span>;
        },
      },
    ],
    [order.orderBy, order.direction, selectedUserIds, users]
  );

  const tableInstance = useTable({ columns, data: users });

  const handleSearchChange = (value) => {
    setSearchBar(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const changeUserStatus = async () => {
    try {
      await Promise.all(
        selectedUserIds.map((userId) =>
          middleware.put("users", {
            user_id: userId,
            account_status: actionType === ACTION_TYPES.ACTIVATE ? ACCOUNT_STATUS.ACTIVE : ACCOUNT_STATUS.SUSPENDED,
          })
        )
      );
      await fetchUsers();
      setSelectedUserIds([]);
      const message = actionType === ACTION_TYPES.ACTIVATE ? 'geactiveerd.' : 'gedeactiveerd.';
      ToastSuccess(`Gebruikers succesvol ${message}`, actionType, 3000);
    } catch (error) {
      ToastError("Er is een fout opgetreden bij het deactiveren van gebruikers");
    }
  };

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);
  const [actionType, setActionType] = useState(null);

  const Action = (method) => {
    if (method === ACTION_TYPES.SUSPEND) {
      handleShow();
      setActionType(ACTION_TYPES.SUSPEND);
    } else if (method === ACTION_TYPES.ACTIVATE) {
      handleShow();
      setActionType(ACTION_TYPES.ACTIVATE);
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton className="bg-white">
          <Modal.Title>Let op!</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-white">
          U gaat {selectedUserIds.length}{" "}
          {selectedUserIds.length === 1 ? "item" : "items"} {actionType === ACTION_TYPES.ACTIVATE ? 'activeren' : 'de-activeren'} . Weet u
          dat zeker?
        </Modal.Body>
        <Modal.Footer className="bg-white">
          <button className="btn bg-danger text-white" onClick={handleClose}>
            Annuleren
          </button>
          <button
            className="btn bg-success text-white"
            onClick={async () => {
              handleClose();
              await changeUserStatus();
            }}
          >
            Ik weet het zeker
          </button>
        </Modal.Footer>
      </Modal>
      <PageTemplate
        navbarTitle={"Gebruikers"}
      >
        <div className="w-100">
          <div className="tableUsers">
            <TablePreset
              data={[
                {
                  selectedIds: selectedUserIds,
                  tableInstance: tableInstance,
                  pagination: pagination,
                  primary_key: "user_id",
                },
              ]}
              tableLoading={loading}
              setPagination={setPagination}
              handleSelection={handleUserSelection}
              searchBar={[{ shown: true, searchFunction: handleSearchChange, placeholder: "Zoeken op gebruiker, email, etc." }]}
              actionOptions={[
                {
                  title: "Activeer",
                  function: () => Action(ACTION_TYPES.ACTIVATE),
                },
                {
                  title: "Deactiveer",
                  function: () => Action(ACTION_TYPES.SUSPEND),
                },
              ]}
              rightButtons={[
                {
                  title: "Nieuwe Gebruiker",
                  function: () =>
                    navigate("/algemene/instellingen/gebruikers/aanmaken"),
                },
              ]}
            />
          </div>
        </div>
      </PageTemplate>
    </>
  );
};

export default Users;